import React, { useState, useRef } from 'react';
import useBackgroundStore from '../store/useBackgroundStore';
import useSceneStore from '../store/useSceneStore';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { uploadFile } from '../api';

const { FiLayers, FiUpload, FiTrash2 } = FiIcons;

const BackgroundPanel = () => {
  const { tool } = useSceneStore();
  const { backgroundItem, selectedBackgroundId, addBackgroundItem, selectBackgroundItem, clearBackground } = useBackgroundStore();
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (file && (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg')) {
      const response = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve({
          status: 'success',
          data: { url: reader.result }
        });
        reader.onerror = () => resolve({
          status: 'error',
          data: null,
          error: 'Nie udało się odczytać podkładu.'
        });
        reader.readAsDataURL(file);
      });

      if (response?.status !== 'success') {
        alert(response?.error || 'Nie udało się wysłać podkładu.');
        return;
      }

      const image = new Image();
      image.onload = () => addBackgroundItem(response.data.url);
      image.src = response.data.url;
    } else {
      alert('Please select a JPG or PNG image file');
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUploadClick = () => { fileInputRef.current?.click(); };

  if (tool !== 'BACKGROUND') return null;

  return (
    <div className="absolute left-20 bottom-4 w-80 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col overflow-hidden z-10" style={{ maxHeight: 'calc(100vh - 100px)' }}>
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiLayers} className="text-[#1E2937] text-lg" />
            <h3 className="font-semibold text-gray-700 text-sm">Podkład</h3>
          </div>
          {backgroundItem && (
            <button 
              onClick={clearBackground} 
              className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
              title="Usuń podkład"
            >
              <SafeIcon icon={FiTrash2} className="text-gray-500 text-sm" />
            </button>
          )}
        </div>
      </div>

      <div className="p-4">
        <div 
          onDragOver={handleDragOver} 
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadClick}
          className={clsx(
            "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all",
            isDragOver ? "border-[#1E2937] bg-gray-100" : "border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100"
          )}
        >
          <input 
            ref={fileInputRef}
            type="file" 
            accept="image/jpeg, image/jpg, image/png"
            onChange={handleFileInputChange}
            className="hidden"
          />
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiUpload} className="text-[#1E2937] text-xl" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">
                Dodaj rysunek podkładowy
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Kliknij lub przeciągnij plik JPG/PNG
              </p>
            </div>
          </div>
        </div>
      </div>

      {backgroundItem && (
        <div className="px-4 pb-4">
          <div className="text-sm font-medium text-gray-700 mb-2">Podkład:</div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            <div 
              onClick={() => selectBackgroundItem(backgroundItem.id)}
              className={clsx(
                "p-2 border rounded-lg cursor-pointer transition-all flex items-center gap-3",
                selectedBackgroundId === backgroundItem.id ? "border-[#1E2937] bg-gray-100" : "border-gray-200 hover:border-gray-300 bg-white"
              )}
            >
              <div className="w-12 h-12 border border-gray-200 rounded overflow-hidden bg-gray-50 flex-shrink-0">
                <img 
                  src={backgroundItem.imageUrl} 
                  alt="Background thumbnail" 
                  className="w-full h-full object-contain" 
                  style={{ opacity: backgroundItem.opacity }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  Podkład {backgroundItem.id.substring(0, 8)}
                </div>
                <div className="text-xs text-gray-500">
                  Skala: {Math.round(backgroundItem.scale * 100)}% • Przezroczystość: {Math.round(backgroundItem.opacity * 100)}%
                </div>
              </div>
              {selectedBackgroundId === backgroundItem.id && (
                <div className="w-2 h-2 bg-[#1E2937] rounded-full flex-shrink-0" />
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
        <div className="text-xs text-gray-500 text-center">
          {backgroundItem ? '1 podkład' : 'Brak podkładu'} • Maks. rozmiar: 10MB
        </div>
      </div>
    </div>
  );
};

export default BackgroundPanel;